import {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterModel,
  GridApi,
  GridState,
} from 'ag-grid-community';
import { camelCase, cloneDeep, isEqual, omit, pick } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { DEFAULT_MAX_ROW_DATA_LENGTH_TO_HIDE_FILTERS } from 'core/constants';
import { AgGridColumnEventSource, AgGridColumnEventType, TableLayoutType } from 'core/enums';
import { mapUpsertCustomLayout } from 'core/mappers';
import { AgGridDataRow } from 'core/types';
import {
  jsonToObject,
  objectToJson,
  removeNullValuesNestedObject,
  updateFloatingFiltersVisibility,
} from 'core/utils';

import { useTableActions } from './table';

interface IProps {
  gridApi: GridApi<AgGridDataRow> | undefined;
  rows?: AgGridDataRow[];
  columnDefs: ColDef[];
  hasLayoutActions: boolean;
  layoutType?: TableLayoutType;
  initialState?: GridState;
  isDataLoadCompleted: boolean;
  defaultFilterModel?: FilterModel;
}

type IColumnDef = ColDef | ColGroupDef;

const isColumnGroup = (column: IColumnDef): column is ColGroupDef => 'children' in column;

/**
 * Puts a saved layout (the grid's column state: colId, width, flex, pinned, hide, in display order)
 * onto the column definitions. A column takes its saved state, a group's columns too, and the
 * columns follow the saved order - a group goes where its first saved column is, within its
 * parent - while a column the layout does not know keeps its default place after the saved ones.
 * The saved flex and width come together: the grid fills the row by flex and falls back to the
 * width for a column it no longer flexes.
 */
export const mergeSavedColumnState = (
  columnDefs: IColumnDef[],
  savedState: ColDef[]
): IColumnDef[] => {
  const savedById = new Map(savedState.map((column, index) => [column.colId, { column, index }]));

  const getPosition = (column: IColumnDef): number =>
    isColumnGroup(column)
      ? Math.min(Infinity, ...(column.children ?? []).map(getPosition))
      : savedById.get(column.colId)?.index ?? Infinity;

  const merge = (columns: IColumnDef[]): IColumnDef[] =>
    columns
      .map((column) =>
        isColumnGroup(column)
          ? { ...column, children: merge(column.children ?? []) }
          : { ...column, ...(savedById.get(column.colId)?.column ?? {}) }
      )
      .map((column, index) => ({ column, index, position: getPosition(column) }))
      .sort((a, b) => a.position - b.position || a.index - b.index)
      .map(({ column }) => column);

  return merge(columnDefs);
};

export const useAgGridLayout = ({
  gridApi,
  rows = undefined,
  columnDefs,
  hasLayoutActions,
  layoutType,
  initialState,
  isDataLoadCompleted,
  defaultFilterModel,
}: IProps) => {
  const { user } = useAuthUser();

  if (hasLayoutActions && !layoutType) {
    throw new Error('layoutType is required when hasLayoutActions is true');
  }

  const customLayoutId = hasLayoutActions && user?.customLayouts?.[camelCase(layoutType)]?.id;
  const customLayoutConfig =
    hasLayoutActions && user?.customLayouts?.[camelCase(layoutType)]?.config;

  const initialFilters = initialState?.filter?.filterModel || {};
  const [filters, setFilters] = useState(initialFilters);
  const filtersCount = Object.keys(filters).length;

  const [layoutChanged, setLayoutChanged] = useState(false);
  const [layoutChangedToDefault, setLayoutChangedToDefault] = useState(!customLayoutConfig);

  const { t } = useTranslation();
  const { upsertCustomLayout } = useTableActions();

  const hasFiltersEnabled = columnDefs.some((column) => column.filter);

  const resetMenuItems = [
    {
      label: t('buttonLabelResetAllFilters'),
      labelExtra: filtersCount > 0 ? t('labelApplied', { count: filtersCount }) : undefined,
      onClick: () => resetFilters(),
      disabled: filtersCount === 0,
      badge: filtersCount > 0,
      hide: !hasFiltersEnabled,
    },
    {
      label: t('buttonLabelResetDefaultFilters'),
      onClick: () => resetFilters(defaultFilterModel),
      disabled: isEqual(
        omit(removeNullValuesNestedObject(cloneDeep(filters)), ['__proto__']),
        omit(removeNullValuesNestedObject(cloneDeep(defaultFilterModel || {})), ['__proto__'])
      ),
      hide: Object.keys(defaultFilterModel || {}).length === 0,
    },
    {
      label: t('buttonLabelResetLayoutToSaved'),
      onClick: () => resetLayoutToSaved(),
      disabled: !layoutChanged || !customLayoutConfig,
      hide: !hasLayoutActions,
    },
    {
      label: t('buttonLabelResetLayoutToDefault'),
      onClick: () => resetLayoutToDefault(),
      disabled: layoutChangedToDefault,
      hide: !hasLayoutActions,
    },
  ];

  const applySavedLayout = () => {
    const mergedColumnDefs = customLayoutConfig
      ? mergeSavedColumnState(columnDefs, jsonToObject(customLayoutConfig))
      : columnDefs;

    let updatedColumnDefs = mergedColumnDefs;

    // Apply floating filter visibility for client tables only (rows !== undefined).
    // Server tables always show floating filters via their baseDefaultColDef.
    if (rows !== undefined) {
      const showFloatingFilter = rows.length > DEFAULT_MAX_ROW_DATA_LENGTH_TO_HIDE_FILTERS;

      updatedColumnDefs = updateFloatingFiltersVisibility(updatedColumnDefs, showFloatingFilter);
    }

    gridApi?.setGridOption('columnDefs', updatedColumnDefs);
    setLayoutChanged(false);
  };

  const onColumnChanged = (
    event?: ColumnResizedEvent | ColumnPinnedEvent | ColumnMovedEvent | ColumnVisibleEvent
  ) => {
    const isColumnResizedAutomaticallyToFitViewport =
      AgGridColumnEventType.COLUMN_RESIZED === event?.type &&
      AgGridColumnEventSource.UI_COLUMN_RESIZED !== event?.source;

    const isTriggeredByAPI = event?.source === AgGridColumnEventSource.API;
    const isTriggeredByGridOptions = event?.source === AgGridColumnEventSource.GRID_OPTIONS_CHANGED;

    if (isColumnResizedAutomaticallyToFitViewport || isTriggeredByAPI || isTriggeredByGridOptions) {
      return;
    }

    setLayoutChanged(true);
    setLayoutChangedToDefault(false);
  };

  const onFilterChanged = (event: FilterChangedEvent) => {
    setFilters(event.api.getFilterModel());
  };

  const getColumnState = () => {
    return gridApi
      ?.getColumnState()
      .map((column) => pick(column, ['colId', 'width', 'flex', 'pinned', 'hide']));
  };

  const onSaveLayout = async () => {
    const { success } = await upsertCustomLayout(
      mapUpsertCustomLayout({
        entity: layoutType,
        config: objectToJson(getColumnState()),
        id: customLayoutId,
      })
    );

    if (success) {
      setLayoutChanged(false);
    }
  };

  const resetLayoutToSaved = async () => {
    applySavedLayout();
    setLayoutChanged(false);
    setLayoutChangedToDefault(false);
  };

  const resetLayoutToDefault = async () => {
    let updatedColumnDefs = columnDefs;

    if (rows !== undefined) {
      const showFloatingFilter = rows.length > DEFAULT_MAX_ROW_DATA_LENGTH_TO_HIDE_FILTERS;

      updatedColumnDefs = updateFloatingFiltersVisibility(updatedColumnDefs, showFloatingFilter);
    }

    gridApi?.setGridOption('columnDefs', updatedColumnDefs);

    setLayoutChanged(true);
    setLayoutChangedToDefault(true);
  };

  const resetFilters = (filterModel = {}) => {
    gridApi?.setFilterModel(filterModel);
    setFilters(filterModel);
  };

  // rows is excluded from deps: floating filter state is handled separately by onRowDataChanged
  // in agGridClientTable, so rerunning applySavedLayout on every row update would be redundant
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(applySavedLayout, [columnDefs, gridApi, customLayoutConfig]);

  useEffect(() => {
    if (isDataLoadCompleted) {
      gridApi?.updateGridOptions({
        onColumnResized: (event) => onColumnChanged(event),
        onColumnVisible: onColumnChanged,
        onColumnMoved: onColumnChanged,
        onColumnPinned: onColumnChanged,
      });
    }
  }, [gridApi, isDataLoadCompleted]);

  return {
    layoutChanged,
    onFilterChanged,
    onSaveLayout,
    resetMenuItems,
  };
};
